import React from "react";
import { RefreshCw, Home, AlertTriangle } from "lucide-react";

/**
 * Branded Error Boundary for Isaiah's World.
 * Shows a recovery UI instead of a blank white screen when a component crashes.
 * Supports retry and return-home actions.
 *
 * Usage:
 *   <ErrorBoundary level="route" section="Learn">
 *     <LearnPage />
 *   </ErrorBoundary>
 *
 * level: "app" (top-level, shows home button) | "route" (section-level, shows retry + home)
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ErrorBoundary:${this.props.section || "app"}]`, error, errorInfo);
    // Report to backend error collector (fire-and-forget)
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      if (API) {
        fetch(`${API}/api/errors/capture`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: (error && error.toString()) || "Unknown error",
            stack: (errorInfo && errorInfo.componentStack) || (error && error.stack) || "",
            url: typeof window !== "undefined" ? window.location.href : "",
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
            source: "boundary",
            section: this.props.section || "app",
            level: "error",
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch { /* no-op */ }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      const { level = "route", section = "" } = this.props;
      const sectionName = section || "this page";

      return (
        <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="error-boundary-fallback">
          <div className="max-w-md w-full text-center">
            {/* Animated icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
              <AlertTriangle size={36} className="text-rose-400 animate-pulse" />
            </div>

            <h2 className="text-white/90 text-xl font-fredoka font-bold mb-2">
              {level === "app" ? "Something went wrong" : `Oops! ${sectionName} hit a bump`}
            </h2>

            <p className="text-white/40 text-sm font-nunito mb-8 leading-relaxed">
              {level === "app"
                ? "Don't worry, your progress is safe. Let's get you back on track."
                : `Something unexpected happened while loading ${sectionName}. This doesn't affect the rest of Isaiah's World.`}
            </p>

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 font-nunito font-bold text-sm hover:bg-blue-500/30 transition-colors min-h-[44px]"
                data-testid="error-retry-btn"
              >
                <RefreshCw size={16} /> Try Again
              </button>
              <button
                onClick={this.handleHome}
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white/60 font-nunito font-bold text-sm hover:bg-white/10 transition-colors min-h-[44px]"
                data-testid="error-home-btn"
              >
                <Home size={16} /> Return Home
              </button>
            </div>

            {/* Dev-only error details */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-white/20 text-[10px] cursor-pointer hover:text-white/40">
                  Developer details
                </summary>
                <pre className="mt-2 p-3 rounded-lg bg-black/30 border border-white/5 text-rose-300/60 text-[10px] overflow-auto max-h-32 font-mono">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
